import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useCustomerBasePaths } from '../../utils/customerBasePaths';
import ShopPhotoPicker from '../../components/ShopPhotoPicker';

export default function CreateCustomer() {
  const navigate = useNavigate();
  const { list } = useCustomerBasePaths();
  const [previewNo, setPreviewNo] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [shopPic, setShopPic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const res = await apiService.customers.nextNumber();
        if (!cancelled) setPreviewNo(res.data?.customerNo || '—');
      } catch {
        if (!cancelled) setPreviewNo('—');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!phone.trim()) {
      setError('Mobile number is required.');
      return;
    }
    if (!/^\d{10}$/.test(phone.trim())) {
      setError('Mobile number must be exactly 10 digits.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), phone: phone.trim() };
      if (shopPic) payload.shopPic = shopPic;
      await apiService.customers.create(payload);
      navigate(list, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6 max-w-lg">
        <div className="flex items-center gap-2 mb-4">
          <Link to={list} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to customers
          </Link>
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-4">Add customer</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer No</label>
            <div className="text-gray-800 font-mono tabular-nums text-lg">
              {previewLoading ? '…' : previewNo}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mobile No *</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <ShopPhotoPicker
            label="Shop pic"
            previewUrl={shopPic}
            onChange={setShopPic}
            onError={setError}
            disabled={submitting}
          />
          {error && <div className="p-2 bg-red-100 text-red-700 text-sm rounded">{error}</div>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 px-4 rounded-lg font-medium min-h-[44px]"
          >
            {submitting ? 'Saving…' : 'Save customer'}
          </button>
        </form>
      </div>
    </div>
  );
}
